import { initializeAuthCallbacks, useGetApiTimesheetsByExternalUniqueId } from '@sit/client-shared';
import { useGetUser } from '@web/api/users/use-get-user';
import { getCompanyFromWebSessionStorage } from '@web/helpers/session';

type DeepLinkUrlData =
  | {
      isPending: true;
      isError: false;
    }
  | {
      isPending: false;
      isError: true;
    }
  | {
      isPending: false;
      isError: false;
      url: string | null;
    };

export const useDeepLinkUrl = (targetUrl: string | null, intacctEntityId: string, token: string | null): DeepLinkUrlData => {
  initializeAuthCallbacks({
    getToken: () => token || undefined,
  });

  const [target, ...[externalId]] = (targetUrl ?? '').split('/');
  const previousSessionCleared = !getCompanyFromWebSessionStorage()?.id;
  const shouldResolve =
    !!targetUrl && !!token && !!target && !!externalId && externalId !== 'new' && ['timesheets', 'staff-timesheets'].includes(target);

  const {
    data: user,
    isPending: userPending,
    isError: userError,
  } = useGetUser({
    enabled: !!token,
  });

  const { data, isError, isPending } = useGetApiTimesheetsByExternalUniqueId(
    {
      pathParams: {
        externalUniqueId: externalId!,
        intacctEntityId,
      },
    },
    { enabled: shouldResolve && previousSessionCleared, retry: false },
  );

  if (!shouldResolve) {
    return {
      isPending: false,
      isError: false,
      url: targetUrl,
    };
  }

  if (isPending) {
    return {
      isPending,
      isError: false,
    };
  }

  if (!data || isError || userError || userPending) {
    return { isPending, isError: true };
  }

  // There's a bug/quirky behaviour in Intacct where when it generates the record URL for a specific timesheet,
  // it doesn't take into consideration whether the timesheet is a "My timesheet" or a "Staff timesheet".
  // Instead, it's ALWAYS a "Staff timesheet" URL.
  // As a result, we have to handle deep linking a bit more carefully.
  // We detect if the timesheet that we are being deep linked into is our own.
  // If it is, we go to the "timesheets" page instead of the "staff-timesheets" page.
  const correctedTarget =
    target === 'staff-timesheets' && !!externalId && externalId !== 'new' && user.employee?.id && data.employeeId === user.employee.id
      ? 'timesheets'
      : target;

  return {
    isError,
    isPending,
    url: `${correctedTarget}/${data.id}`,
  };
};
